import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Flag,
  Globe,
  Icon,
  LogOut,
  MessageSquarePlus,
  ShieldAlert,
} from "@norican/icons";
import { Avatar, Button, Menu, Typography } from "@norican/react";
import { Link } from "react-router";

import { SUSPICIOUS_EMAIL_PATH } from "@/features/emergency/lib/priority";
import { useMessages } from "@/features/messages/api";
import { type AppLanguage, useLanguage, useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { useSignOut } from "./use-sign-out";

/** "Astrid Lindqvist" → "AL"; falls back to the first letter of the email. */
function initialsFor(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? email.charAt(0);
  const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return `${first}${second}`.toUpperCase();
}

/** The built-in "How to use the Norican IT HelpDesk" notice (`builtin:help`). */
const HELP_EXTERNAL_ID = "builtin:help";

export interface UserMenuProps {
  className?: string;
}

/**
 * Signed-in identity and the personal things, in the header (6 Oct, decision 35):
 * who you are, Feedback, Help, Language and Sign out.
 *
 * The trigger renders a design system `Button` through Base UI's `render` prop
 * rather than nesting a button inside a button, so the menu keeps its
 * `aria-haspopup`/`aria-expanded` wiring and the roving focus that comes with
 * it. The user's name is hidden below `sm` — the avatar and the accessible
 * name carry it there. Language is here as well as on the globe, and is the
 * only way to it on a phone, where the globe is hidden to fit the header.
 */
export function UserMenu({ className }: UserMenuProps) {
  const { t } = useT();
  const { data: me } = useMe();
  const { signOut, isPending: signingOut } = useSignOut();
  const { language, languages, changeLanguage } = useLanguage();

  const email = me?.email ?? "";
  const name = [me?.first_name, me?.last_name].filter(Boolean).join(" ") || email;
  const roleLabel =
    me?.role === "admin"
      ? t("user.role.admin")
      : me?.role === "migrator"
        ? t("user.role.migrator")
        : t("user.role.user");

  return (
    <Menu.Root>
      <Menu.Trigger
        className={className}
        render={
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            aria-label={`${t("user.menu")}: ${name}`}
            endIcon={<Icon icon={ChevronDown} size="sm" />}
            // No chevron below `sm`: at 390px the header's right cluster
            // (tickets, this) could clip the avatar. The avatar is the
            // affordance on a phone; the chevron's span is the last child.
            className="max-sm:[&>span:last-child]:hidden"
          />
        }
      >
        <span className="flex items-center gap-2">
          <Avatar.Root size="sm">
            <Avatar.Fallback>{initialsFor(name, email)}</Avatar.Fallback>
          </Avatar.Root>
          <span className="hidden max-w-40 truncate sm:inline">{name}</span>
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="end">
          <Menu.Popup width="content">
            <Menu.Group>
              <Menu.GroupLabel>{t("user.signedInAs")}</Menu.GroupLabel>
              <div className="flex flex-col gap-1 px-3 pb-2">
                <Typography variant="body-sm">{name}</Typography>
                <Typography variant="caption" tone="muted">
                  {email}
                </Typography>
                <Typography variant="caption" tone="muted">
                  {roleLabel}
                </Typography>
              </div>
            </Menu.Group>
            <Menu.Separator />
            {/* Feedback goes to the chat, prefilled: the chat is the front
                door, and the "HelpDesk feedback & ideas" request is not built
                yet. When it is, point this at it. */}
            <Menu.Item render={<Link to="/ask" state={{ prefill: t("user.feedbackPrefill") }} />}>
              <span className="flex items-center gap-2">
                <Icon icon={MessageSquarePlus} size="sm" />
                {t("user.feedback")}
              </span>
            </Menu.Item>
            <HelpItem />
            {/* 19 Oct (incident 940): one suspicious email has its own page,
                beside Help; also linked from the Emergency form and the chat. */}
            <Menu.Item render={<Link to={SUSPICIOUS_EMAIL_PATH} />}>
              <span className="flex items-center gap-2">
                <Icon icon={ShieldAlert} size="sm" />
                {t("user.reportSuspiciousEmail")}
              </span>
            </Menu.Item>
            {languages.length > 1 ? (
              <Menu.SubmenuRoot>
                <Menu.SubmenuTrigger>
                  <span className="flex w-full items-center gap-2">
                    <Icon icon={Globe} size="sm" />
                    <span className="flex-1">{t("user.language")}</span>
                    <Icon icon={ChevronRight} size="sm" />
                  </span>
                </Menu.SubmenuTrigger>
                <Menu.Portal>
                  <Menu.Positioner sideOffset={4} align="start">
                    <Menu.Popup width="content">
                      <Menu.RadioGroup
                        value={language}
                        onValueChange={(next) => changeLanguage(next as AppLanguage)}
                      >
                        {languages.map((entry) => (
                          <Menu.RadioItem key={entry.tag} value={entry.tag} closeOnClick>
                            <span className="flex w-full items-center gap-2">
                              <Flag code={entry.flagCode} size="sm" />
                              <span className="flex-1">{entry.endonym}</span>
                              <Menu.RadioItemIndicator>
                                <Icon icon={Check} size="sm" />
                              </Menu.RadioItemIndicator>
                            </span>
                          </Menu.RadioItem>
                        ))}
                      </Menu.RadioGroup>
                    </Menu.Popup>
                  </Menu.Positioner>
                </Menu.Portal>
              </Menu.SubmenuRoot>
            ) : null}
            <Menu.Separator />
            <Menu.Item onClick={() => void signOut()} disabled={signingOut}>
              <span className="flex items-center gap-2">
                <Icon icon={LogOut} size="sm" />
                {signingOut ? t("user.signingOut") : t("user.signOut")}
              </span>
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/**
 * "Help": the built-in "How to use" guide, a notice in the Message Center.
 *
 * Its id is the database row's, so it is looked up in the notice list (the
 * same query and cache as the Message Center). Mounted only while the menu is
 * open, so the header costs no call on pages that never open it. Until the
 * list answers, or if an admin has hidden the guide, it opens the Message
 * Center, which is where the footer sends people for help too.
 */
function HelpItem() {
  const { t } = useT();
  const { data } = useMessages();
  const guide = data?.find((message) => message.external_id === HELP_EXTERNAL_ID);
  const to = guide === undefined ? "/messages" : `/messages/${guide.message_id}`;
  return (
    <Menu.Item render={<Link to={to} />}>
      <span className="flex items-center gap-2">
        <Icon icon={CircleHelp} size="sm" />
        {t("user.help")}
      </span>
    </Menu.Item>
  );
}
